<template>
	<ValidationForm #default="{ handleSubmit }">
		<ConsentCollectionLayout
			v-if="consentCollectionWidgetTranslation"
			:title="pageTitle"
			caption="Manage Consent Collection Widget translation"
		>
			<template #content>
				<div class="cassie-vertical-md">
					<SectionCard>
						<template #title>
							Details
						</template>
						<template #body>
							<v-row dense>
								<v-col cols="6">
									<TextField
										:value="consentCollectionWidgetTranslation.inlineModel.name"
										label="Consent Collection Widget Name"
										disabled
									/>
								</v-col>
								<v-col cols="6">
									<TextField
										:value="consentCollectionWidgetTranslation.inlineModel.brandName"
										:label="'Brand' | useLabels"
										disabled
									/>
								</v-col>
							</v-row>
							<v-row dense>
								<v-col cols="6">
									<TextField
										:value="consentCollectionWidgetTranslation.inlineModel.languageName"
										label="Language"
										disabled
									/>
								</v-col>
							</v-row>
						</template>
					</SectionCard>
					<SectionCard>
						<template #title>
							Success Message Translations
						</template>
						<template #body>
							<v-row dense>
								<v-col cols="6">
									<TextArea
										:value="consentCollectionWidgetTranslation.inlineModel.defaultOutcomeSuccessInlineHeadline"
										label="Default Success Headline"
										test-id="consent_collection_widget_default_success_headline"
										disabled
										rows="1"
										auto-grow
										dense
									/>
								</v-col>
								<v-col cols="6">
									<TextArea
										:value.sync="consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineHeadline"
										label="Success Headline Translation"
										test-id="consent_collection_widget_success_headline"
										:disabled="!userFullPermissions"
										:rules="{ max: 2000 }"
										rows="1"
										auto-grow
										dense
										@input="updateSuccessMessageTranslation('translatedOutcomeSuccessInlineHeadline', $event)"
									/>
								</v-col>
							</v-row>
							<v-row dense>
								<v-col cols="6">
									<TextArea
										:value="consentCollectionWidgetTranslation.inlineModel.defaultOutcomeSuccessInlineMessage"
										label="Default Success Message"
										test-id="consent_collection_widget_default_success_message"
										disabled
										rows="1"
										auto-grow
										dense
									/>
								</v-col>
								<v-col cols="6">
									<TextArea
										:value.sync="consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineMessage"
										label="Success Message Translation"
										test-id="consent_collection_widget_success_message"
										:disabled="!userFullPermissions"
										:rules="{ max: 2000 }"
										rows="1"
										auto-grow
										dense
										@input="updateSuccessMessageTranslation('translatedOutcomeSuccessInlineMessage', $event)"
									/>
								</v-col>
							</v-row>
						</template>
					</SectionCard>
					<div>
						<Tabs
							v-if="schemaTabs.length > 1"
							:tab.sync="tab"
							:tabs="schemaTabs"
							embedded
							small
						/>
						<TabItems
							:tab="tab"
							:tabs="schemaTabs"
						>
							<div
								v-for="({ key, slot }) in schemaTabs"
								:key="key"
								:slot="slot"
								class="cassie-vertical-md"
							>
								<TranslationManageTab
									v-if="translatedSchema"
									:preference-page-translation="consentCollectionWidgetTranslation"
									:user-full-permissions="userFullPermissions"
									:area="key"
									:area-schema="consentCollectionWidgetTranslation.defaultSchema.schema[key]"
									:area-translation-schema="consentCollectionWidgetTranslation.translatedSchema.schema[key]"
									:translated-schema="translatedSchema"
									@update-translation="updateTranslation"
								/>
							</div>
						</TabItems>
					</div>
				</div>
			</template>
			<template #footer>
				<PageActionRow>
					<SecondaryActionButton
						test-id="manage_consent_collection_widget_translation_back_to_overview_button"
						@click="backToOverview"
					>
						Back To Overview
					</SecondaryActionButton>
					<template #actions>
						<SecondaryActionButton
							test-id="manage_consent_collection_widget_translation_back_to_overview_button"
							@click="backToOverview"
						>
							Back To Overview
						</SecondaryActionButton>
						<v-spacer />
						<SecondaryActionButton
							test-id="manage_consent_collection_widget_translation_preview_button"
							@click="previewTranslation"
						>
							Preview
						</SecondaryActionButton>
						<PrimaryActionButton
							v-if="userFullPermissions"
							class="ml-2"
							test-id="manage_consent_collection_widget_translation_finish_button"
							@click="handleSubmit(saveTranslation)"
						>
							{{ isEdit ? 'Save Changes' : 'Create' }}
						</PrimaryActionButton>
					</template>
				</PageActionRow>
			</template>
		</ConsentCollectionLayout>
		<TranslationPreviewModal
			v-if="showPreviewModal"
			:channels="channels"
			:language-id="languageId"
			:language-name="translationPreviewLanguage"
			:name="translationPreviewName"
			@close="closePreviewModal"
		/>
	</ValidationForm>
</template>

<script>
import { mapGetters } from 'vuex'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import ConsentCollectionLayout from '../consent-collection-layout.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import TextArea from '../../../../../../shared/components/textarea.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import Tabs from '../../../../../../shared/components/tabs.vue'
import TabItems from '../../../../../../shared/components/tab-items.vue'
import TranslationManageTab from '../translation-manage-tab.vue'
import TranslationPreviewModal from '../translation-preview-modal.vue'
import { PREF_WIDGETS_AREA_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { CONSENT_COLLECTION_WIDGETS } from '../../../../router/route-names.js'
import { getConsentCollectionWidgetTranslation, putConsentCollectionWidgetTranslation, postConsentCollectionWidgetTranslation } from '../../../../../../shared/utils/api/consent-collection-widgets.js'
import { getPreferenceChannels } from '../../../../../../shared/utils/api/channels.js'
import { postSchema, putSchema, getTheme, previewSchemaTranslation } from '../../../../../../shared/utils/api/form-builder.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { brandOptions } from '../../../../../../shared/state/brands.js'
import { getEnv } from '../../../../../../shared/utils/runtime-settings.js'
import { defaultVueformBuilderFormat } from '../../../../../../shared/utils/vueform-builder-utils.js'
export default {
	components: {
		ValidationForm,
		ConsentCollectionLayout,
		SectionCard,
		TextField,
		TextArea,
		PageActionRow,
		PrimaryActionButton,
		SecondaryActionButton,
		Tabs,
		TabItems,
		TranslationManageTab,
		TranslationPreviewModal
	},
	props: {
		id: {
			type: String,
			required: true
		},
		languageId: {
			type: Number,
			required: true
		}
	},
	setup () {
		const isFormBuilderScriptLoaded = Array.from(document.getElementsByTagName('script')).some(script => script.src === getEnv('VUE_APP_FORMBUILDER_URL'))
		if (!isFormBuilderScriptLoaded) {
			const formBuilderScript = document.createElement('script')
			formBuilderScript.src = getEnv('VUE_APP_FORMBUILDER_URL')
			document.head.appendChild(formBuilderScript)
		}
	},
	data () {
		return {
			consentCollectionWidgetTranslation: null,
			formBuilderThemeGUID: null,
			channels: [],
			tab: '0',
			translatedSchema: {},
			showPreviewModal: false,
			translationSchemaGUID: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		translationPreviewName () {
			return this.consentCollectionWidgetTranslation?.inlineModel?.name
		},
		translationPreviewLanguage () {
			return this.consentCollectionWidgetTranslation?.inlineModel?.languageName
		},
		isEdit () {
			return this.consentCollectionWidgetTranslation?.translatedSchema.formBuilderSchemaId
		},
		pageTitle () {
			return `Manage ${this.translationPreviewName} Translation - ${this.translationPreviewLanguage}`
		},
		userFullPermissions () {
			return this.productAreaPermission(PREF_WIDGETS_AREA_FULL_PERMISSIONS) && this.userHasBrand
		},
		userHasBrand () {
			const brands = brandOptions.value.map(({ value }) => value)
			return brands.includes(this.consentCollectionWidgetTranslation?.inlineModel?.brandId)
		},
		schemaTabs () {
			const tabs = []
			const schema = this.consentCollectionWidgetTranslation?.translatedSchema?.schema
			if (schema) {
				for (const key in schema) {
					if (schema[key].length > 0) {
						tabs.push({
							title: this.tabName(key),
							key: key,
							slot: tabs.length.toString()
						})
					}
				}
			}
			return tabs
		}
	},
	async created () {
		await this.getConsentCollectionWidgetTranslation()
	},
	methods: {
		async getConsentCollectionWidgetTranslation () {
			const { consentCollectionWidgetTranslation } = await getConsentCollectionWidgetTranslation(this.id, this.languageId)
			this.consentCollectionWidgetTranslation = consentCollectionWidgetTranslation
			this.translatedSchema = await this.convertTranslatedSchema()
			if (this.isEdit) {
				this.translationSchemaGUID = this.consentCollectionWidgetTranslation.translatedSchema.formBuilderSchemaId
			}
		},
		tabName (key) {
			switch (key) {
				case 'components':
					return 'Components'
				case 'HTML':
					return 'HTML'
				case 'dataSubjects':
					return 'Data Subject'
				default:
					return key
			}
		},
		convertTranslatedSchema () {
			const translatedSchema = this.consentCollectionWidgetTranslation.translatedSchema.schema
			const components = translatedSchema.components || []
			const HTML = translatedSchema.HTML || []
			const dataSubjects = translatedSchema.dataSubjects || []

			const translationObj = {}
			components.forEach(component => {
				if (component.name) {
					translationObj[component.name] = component
				}
			})

			HTML.forEach(html => {
				if (html.name) {
					translationObj[html.name] = html
				}
			})

			dataSubjects.forEach(dataSubject => {
				if (dataSubject.name) {
					translationObj[dataSubject.name] = dataSubject
				}
			})
			return translationObj
		},
		updateTranslation (key, property, value) {
			this.$set(this.translatedSchema[key], property, value)
		},
		updateSuccessMessageTranslation (key, value) {
			this.$set(this.consentCollectionWidgetTranslation.inlineModel, key, value)
		},
		backToOverview () {
			this.$router.push({
				name: CONSENT_COLLECTION_WIDGETS,
				params: {
					accessedViaTranslations: true
				}
			})
			localStorage.removeItem('vueform-builder')
		},
		nextStep () {
			this.step++
		},
		previousStep () {
			this.step--
		},
		async previewTranslation () {
			if (this.channels.length === 0) {
				const { data: { channels } } = await getPreferenceChannels()
				this.channels = channels
			}
			const builderConfig = { ...defaultVueformBuilderFormat }
			const previewPayload = {
				json: JSON.stringify(this.translatedSchema),
				defaultLanguageSchemaGuid: this.consentCollectionWidgetTranslation.defaultSchema.formBuilderSchemaId,
				languageId: this.languageId
			}
			const translationPreview = await previewSchemaTranslation(previewPayload).then(response => {
				return response.data.translatedSchemaJson
			})
			builderConfig.schema = JSON.parse(translationPreview)
			const { data } = await getTheme(this.consentCollectionWidgetTranslation.inlineModel.formBuilderThemeGUID)
			builderConfig.theme = JSON.parse(data.json)
			localStorage.setItem('vueform-builder', JSON.stringify(builderConfig))
			this.showPreviewModal = true
		},
		closePreviewModal () {
			this.showPreviewModal = false
			localStorage.removeItem('vueform-builder')
		},
		async saveTranslation () {
			await this.saveSchema()
			await this.saveWidgetTranslation()
			showSnackbar('Consent Collection Widget Translation Saved')
			this.backToOverview()
		},
		async saveWidgetTranslation () {
			await this.isEdit ? this.putConsentCollectionWidgetTranslation() : this.postConsentCollectionWidgetTranslation()
		},
		async putConsentCollectionWidgetTranslation () {
			const payload = {
				languageId: this.languageId,
				outcomeSuccessInlineHeadline: this.consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineHeadline ?? '',
				outcomeSuccessInlineMessage: this.consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineMessage ?? '',
				translationSchemaGUID: this.translationSchemaGUID
			}
			await putConsentCollectionWidgetTranslation(this.id, this.languageId, payload)
		},
		async postConsentCollectionWidgetTranslation () {
			const payload = {
				consentCollectionWidgetId: this.consentCollectionWidgetTranslation.inlineModel.consentCollectionWidgetID,
				languageId: this.consentCollectionWidgetTranslation.inlineModel.languageId,
				outcomeSuccessInlineHeadline: this.consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineHeadline,
				outcomeSuccessInlineMessage: this.consentCollectionWidgetTranslation.inlineModel.translatedOutcomeSuccessInlineMessage
			}
			await postConsentCollectionWidgetTranslation(payload)
		},
		async saveSchema () {
			const schemaTranslationPayload = {
				brandId: this.consentCollectionWidgetTranslation.inlineModel.brandId,
				json: JSON.stringify(this.translatedSchema),
				isTemplate: false,
				languageId: this.languageid
			}
			if (!this.isEdit) {
				schemaTranslationPayload.defaultLanguageSchemaGUID = this.consentCollectionWidgetTranslation.defaultSchema.formBuilderSchemaId
				schemaTranslationPayload.languageId = this.languageId
				await postSchema(schemaTranslationPayload).then(response => {
					this.translationSchemaGUID = response.data.id
				})
			} else {
				schemaTranslationPayload.defaultLanguageSchemaGuid = this.consentCollectionWidgetTranslation.defaultSchema.formBuilderSchemaId
				await putSchema(this.consentCollectionWidgetTranslation.translatedSchema.formBuilderSchemaId, schemaTranslationPayload)
			}
		}
	}
}
</script>
